import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import GetData from "../../services/GetData.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import SelectableCell, { openSelectableCell } from "../Helpers/SelectableCell.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { RETURNS_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import { currencyFormatter } from "../Helpers/CurrencyFormat.js";
import { SingleSelectField, LookupField } from "../Products/TypedFields.jsx";
import {
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "../Products/catalogGridStyles.js";
import "../Products/catalog.css";

const STATUS_CHOICES = [
    "Pending Our Response",
    "Pending Customer Ship",
    "Pending ShipStation Response",
    "Pending UPS Response",
    "Pending Merchant Decision",
    "Non Refunded",
    "Refunded",
];

const CLAIM_REASONS = [
    "Item not As Described",
    "General Return",
    "Item not Shipped",
    "Item not Received by Buyer",
    "Dispute Return Condition",
    "Lost in Transit",
    "No Longer Wanted",
    "Lost After Delivery",
    "Incorrect item received",
    "Item Arrived Damaged",
    "Lower Price",
    "Defective/Broken",
    "Bought Somewhere Else",
    "Missing Items from Box",
    "Late to arrive",
    "Item and Shipping Box Damaged",
    "Difficult to set-up/Notcompatible",
];

const FILE_CLAIM = ["Not Needed", "Pending", "Filed", "Resolved"];
const RETURNED_STATUS = ["Pushed", "Not Pushed"];

const EMPTY_FORM = {
    rmaWalmart: "",
    disputeNumber: "",
    returnTrackingId: "",
    returnId: "",
    fileDate: "",
    quantity: "",
    status: "",
    claimReason: "",
    fileClaim: "",
    returnedItemStatus: "",
    checkAmount: "",
    orderNumber: "",
    orderAirtableId: "",
    skuName: "",
    itemName: "",
    platform: "",
};

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

function formatDateInput(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
}

function money(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return currencyFormatter(n, 2);
}

function channelChipSx(channel) {
    const key = String(channel || "").toLowerCase();
    const base = { height: 24, fontWeight: 600, fontSize: 12, borderRadius: "8px" };
    if (key === "ebay") {
        return { ...base, backgroundColor: "#fef3c7", color: "#854d0e", border: "1px solid #fcd34d" };
    }
    if (key === "walmart") {
        return { ...base, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    }
    return { ...base, backgroundColor: "#f1f5f9", color: "#475569" };
}

function formFromReturn(item) {
    if (!item) return { ...EMPTY_FORM };
    return {
        rmaWalmart: item.rmaWalmart || "",
        disputeNumber: item.disputeNumber || "",
        returnTrackingId: item.returnTrackingId || "",
        returnId: item.returnId || "",
        fileDate: formatDateInput(item.fileDate),
        quantity: item.quantity == null ? "" : String(item.quantity),
        status: item.status || "",
        claimReason: item.claimReason || "",
        fileClaim: item.fileClaim || "",
        returnedItemStatus: item.returnedItemStatus || "",
        checkAmount: item.checkAmount == null ? "" : String(item.checkAmount),
        orderNumber: item.orderNumber || "",
        orderAirtableId: item.orderAirtableIds?.[0] || "",
        skuName: item.skuName || item.skuHelper || "",
        itemName: item.itemName || "",
        platform: item.platform || "",
    };
}

export default function ReturnsGrid({ returns, loading, onSaved }) {
    const [searchInput, setSearchInput] = useState("");
    const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [orderOptions, setOrderOptions] = useState([]);
    const [orderInput, setOrderInput] = useState("");
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchingOrders, setSearchingOrders] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });
    const q = searchInput.trim().toLowerCase();
    const { apply: applyFilter, barProps: filterBar } = useGridFilter(RETURNS_FILTER_FIELDS);

    const selectedIds = useMemo(() => [...(selectionModel?.ids ?? [])], [selectionModel]);

    const rows = useMemo(
        () =>
            applyFilter(
                (returns || [])
                    .map((item) => ({
                        id: String(item._id),
                        rma: item.rmaWalmart || "",
                        returnId: item.returnId || "",
                        orderNumber: item.orderNumber || "",
                        platform: item.platform || "",
                        status: item.status || "",
                        claimReason: item.claimReason || "",
                        fileClaim: item.fileClaim || "",
                        fileDate: formatDate(item.fileDate),
                        quantity: item.quantity ?? "",
                        skuName: item.skuName || item.skuHelper || "",
                        itemName: item.itemName || "",
                        returnTrackingId: item.returnTrackingId || "",
                        returnedItemStatus: item.returnedItemStatus || "",
                        totalValue: item.totalValue,
                        disputeNumber: item.disputeNumber || "",
                        raw: item,
                    }))
                    .filter((row) => {
                        if (!q) return true;
                        return [
                            row.rma,
                            row.returnId,
                            row.orderNumber,
                            row.skuName,
                            row.itemName,
                            row.status,
                            row.disputeNumber,
                        ]
                            .join(" ")
                            .toLowerCase()
                            .includes(q);
                    })
            ),
        [returns, q, applyFilter]
    );

    const openCreate = () => {
        setEditingId(null);
        setForm({ ...EMPTY_FORM });
        setSelectedOrder(null);
        setOrderInput("");
        setOrderOptions([]);
        setDialogOpen(true);
    };

    const openEdit = (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        const row = rows.find((item) => item.id === String(id));
        if (!row?.raw) return;
        const next = formFromReturn(row.raw);
        setEditingId(row.id);
        setForm(next);
        setSelectedOrder(
            next.orderNumber
                ? {
                      id: next.orderAirtableId || next.orderNumber,
                      orderNumber: next.orderNumber,
                      channel: next.platform,
                      itemTitle: next.itemName,
                      sku: next.skuName,
                  }
                : null
        );
        setOrderInput(next.orderNumber || "");
        setOrderOptions([]);
        setDialogOpen(true);
    };

    useEffect(() => {
        if (!dialogOpen) return undefined;
        const q = orderInput.trim();
        if (q.length < 2) {
            setOrderOptions([]);
            return undefined;
        }
        const timer = setTimeout(async () => {
            setSearchingOrders(true);
            const { getData } = await GetData(
                `broylesllc/orders/search?q=${encodeURIComponent(q)}`
            );
            setOrderOptions(Array.isArray(getData) ? getData : []);
            setSearchingOrders(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [orderInput, dialogOpen]);
    const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
    const setSelect = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

    const save = async () => {
        if (!form.rmaWalmart.trim() && !String(form.returnId || "").trim()) {
            setSnack({
                open: true,
                color: "error",
                message: "RMA or Return ID is required.",
            });
            return;
        }
        setSaving(true);
        const payload = {
            rmaWalmart: form.rmaWalmart.trim(),
            disputeNumber: form.disputeNumber,
            returnTrackingId: form.returnTrackingId,
            returnId: String(form.returnId || "").trim(),
            fileDate: form.fileDate ? new Date(form.fileDate).toISOString() : null,
            quantity: form.quantity === "" ? null : Number(form.quantity),
            status: form.status,
            claimReason: form.claimReason,
            fileClaim: form.fileClaim,
            returnedItemStatus: form.returnedItemStatus,
            checkAmount: form.checkAmount === "" ? null : Number(form.checkAmount),
            orderNumber: selectedOrder?.orderNumber || form.orderNumber || "",
            orderAirtableIds: selectedOrder?.id?.startsWith?.("rec")
                ? [selectedOrder.id]
                : form.orderAirtableId
                  ? [form.orderAirtableId]
                  : [],
            platform: selectedOrder?.channel || form.platform || "",
            itemName: selectedOrder?.itemTitle || form.itemName || "",
            skuName: selectedOrder?.sku || form.skuName || "",
            skuHelper: selectedOrder?.sku || form.skuName || "",
            orderDate: selectedOrder?.orderDate || null,
            shipToZip: selectedOrder?.shipToZip || "",
            originalTrackingId: selectedOrder?.trackingNumber || "",
        };
        const result = editingId
            ? await PutData(payload, `broylesllc/returns/${editingId}`)
            : await PostData(payload, "broylesllc/returns");
        setSaving(false);
        if (result.success) {
            setDialogOpen(false);
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Return updated." : "Return created.",
            });
            onSaved?.();
        } else {
            setSnack({
                open: true,
                color: "error",
                message: result?.data?.message || "Save failed.",
            });
        }
    };

    const layout = useGridLayout(GRID_LAYOUT_KEYS.returns, {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = [
        { field: "rma", headerName: "RMA", minWidth: 160, flex: 0.8 },
        { field: "returnId", headerName: "Return ID", minWidth: 140 },
        {
            field: "orderNumber",
            headerName: "Order",
            minWidth: 150,
            renderCell: (params) => {
                const label = params.value || "";
                if (!label) {
                    return (
                        <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                            —
                        </Typography>
                    );
                }
                const query = new URLSearchParams({
                    order: label,
                    fromReturn: params.row.id,
                });
                return (
                    <Link
                        component={RouterLink}
                        to={`/orders?${query.toString()}`}
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ fontWeight: 600, color: "#0d9488", fontSize: 13 }}
                    >
                        {label}
                    </Link>
                );
            },
        },
        {
            field: "platform",
            headerName: "Platform",
            minWidth: 120,
            renderCell: (params) => {
                const text = params.value || "";
                if (!text) {
                    return (
                        <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                            —
                        </Typography>
                    );
                }
                return <Chip size="small" label={text} sx={channelChipSx(text)} />;
            },
        },
        { field: "status", headerName: "Status", minWidth: 200, type: "singleSelect", editable: true, valueOptions: STATUS_CHOICES, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "claimReason", headerName: "Claim reason", minWidth: 220, flex: 1, type: "singleSelect", editable: true, valueOptions: CLAIM_REASONS, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "fileClaim", headerName: "File claim", minWidth: 140, type: "singleSelect", editable: true, valueOptions: FILE_CLAIM, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "returnedItemStatus", headerName: "Returned item", minWidth: 150, type: "singleSelect", editable: true, valueOptions: RETURNED_STATUS, renderCell: (params) => <SelectableCell value={params.value} /> },
        { field: "fileDate", headerName: "File date", minWidth: 120 },
        { field: "quantity", headerName: "Qty", width: 70, align: "right", headerAlign: "right" },
        { field: "skuName", headerName: "SKU", minWidth: 160 },
        { field: "itemName", headerName: "Item", minWidth: 200, flex: 1.2 },
        { field: "returnTrackingId", headerName: "Return tracking", minWidth: 160 },
        {
            field: "totalValue",
            headerName: "Total value",
            width: 120,
            align: "right",
            headerAlign: "right",
            valueFormatter: (value) => money(value) || "—",
        },
    ];

    const columns = layout.applyWidths(baseColumns);

    return (
        <>
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search RMA, order, SKU…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <span className="catalog-selection-hint">
                        {rows.length} return{rows.length === 1 ? "" : "s"}
                    </span>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add return
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={loading}
                        rows={rows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortModel={layout.sortModel}
                        onSortModelChange={layout.onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onCellClick={(params) =>
                            openSelectableCell(params, [
                                "status",
                                "claimReason",
                                "fileClaim",
                                "returnedItemStatus",
                            ])
                        }
                        onCellDoubleClick={(params) => {
                            if (["status", "claimReason", "fileClaim", "returnedItemStatus"].includes(params.field)) return;
                            openEdit(params.id);
                        }}
                        processRowUpdate={async (newRow, oldRow) => {
                            const payload = {};
                            for (const key of ["status", "claimReason", "fileClaim", "returnedItemStatus"]) {
                                if ((newRow[key] || "") !== (oldRow[key] || "")) payload[key] = newRow[key] || "";
                            }
                            if (!Object.keys(payload).length) return oldRow;
                            const result = await PutData(payload, `broylesllc/returns/${newRow.id}`);
                            if (!result.success) {
                                const message = result?.data?.message || "Save failed.";
                                setSnack({ open: true, color: "error", message });
                                throw new Error(message);
                            }
                            setSnack({ open: true, color: "success", message: "Saved." });
                            onSaved?.();
                            return { ...newRow, raw: { ...newRow.raw, ...payload } };
                        }}
                        onProcessRowUpdateError={() => {}}
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                        slotProps={appDataGridSlotProps}
                        sx={catalogDataGridSx}
                        localeText={{ noRowsLabel: "No returns yet." }}
                    />
                </div>
            </div>

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                scroll="paper"
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingId ? "Edit return" : "Add return"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        <TextField
                            label="RMA (Walmart)"
                            size="small"
                            value={form.rmaWalmart}
                            onChange={setField("rmaWalmart")}
                            fullWidth
                            helperText="Provide RMA and/or Return ID"
                        />
                        <Autocomplete
                            size="small"
                            options={orderOptions}
                            loading={searchingOrders}
                            value={selectedOrder}
                            inputValue={orderInput}
                            onInputChange={(_event, value, reason) => {
                                if (reason === "reset") return;
                                setOrderInput(value);
                            }}
                            onChange={(_event, order) => {
                                setSelectedOrder(order);
                                setOrderInput(order?.orderNumber || "");
                                if (!order) return;
                                setForm((prev) => ({
                                    ...prev,
                                    orderNumber: order.orderNumber || "",
                                    orderAirtableId: order.id || "",
                                    platform: order.channel || "",
                                    itemName: order.itemTitle || "",
                                    skuName: order.sku || "",
                                    quantity:
                                        prev.quantity ||
                                        (order.quantity == null ? "" : String(order.quantity)),
                                }));
                            }}
                            getOptionLabel={(option) => option?.orderNumber || ""}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            filterOptions={(options) => options}
                            noOptionsText={
                                orderInput.trim().length < 2
                                    ? "Type at least 2 characters"
                                    : "No matching orders"
                            }
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    <span>
                                        <strong>{option.orderNumber}</strong>
                                        {option.channel ? ` · ${option.channel}` : ""}
                                        {option.itemTitle ? ` · ${option.itemTitle}` : ""}
                                    </span>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Match order"
                                    placeholder="Search order number"
                                />
                            )}
                        />
                        <TextField
                            label="Dispute number"
                            size="small"
                            value={form.disputeNumber}
                            onChange={setField("disputeNumber")}
                            fullWidth
                        />
                        <TextField
                            label="Return tracking ID"
                            size="small"
                            value={form.returnTrackingId}
                            onChange={setField("returnTrackingId")}
                            fullWidth
                        />
                        <TextField
                            label="Return ID"
                            size="small"
                            value={form.returnId}
                            onChange={setField("returnId")}
                            fullWidth
                        />
                        <TextField
                            label="File date"
                            size="small"
                            type="date"
                            value={form.fileDate}
                            onChange={setField("fileDate")}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Quantity"
                            size="small"
                            type="number"
                            value={form.quantity}
                            onChange={setField("quantity")}
                            fullWidth
                        />
                        <SingleSelectField
                            label="Status"
                            value={form.status}
                            onChange={setSelect("status")}
                            choices={STATUS_CHOICES}
                        />
                        <SingleSelectField
                            label="Claim reason"
                            value={form.claimReason}
                            onChange={setSelect("claimReason")}
                            choices={CLAIM_REASONS}
                        />
                        <SingleSelectField
                            label="File claim"
                            value={form.fileClaim}
                            onChange={setSelect("fileClaim")}
                            choices={FILE_CLAIM}
                        />
                        <SingleSelectField
                            label="Returned item status"
                            value={form.returnedItemStatus}
                            onChange={setSelect("returnedItemStatus")}
                            choices={RETURNED_STATUS}
                        />
                        <TextField
                            label="Check amount"
                            size="small"
                            type="number"
                            value={form.checkAmount}
                            onChange={setField("checkAmount")}
                            fullWidth
                        />
                        {editingId ? (
                            <>
                                <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
                                    From the order
                                </Typography>
                                <LookupField label="Order number" value={form.orderNumber} />
                                <LookupField label="Platform" value={form.platform} />
                                <LookupField label="SKU" value={form.skuName} />
                                <LookupField label="Item" value={form.itemName} />
                            </>
                        ) : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={save}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </>
    );
}
